import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import {
  RebacExplorerClient,
  FgaTypeDefinition,
} from '../../api/RebacExplorerClient';

// ─── FGA DSL converter ───────────────────────────────────────────────────────

function typeRefToString(ref: any): string {
  if (ref.wildcard !== undefined) return `${ref.type}:*`;
  if (ref.relation) return `${ref.type}#${ref.relation}`;
  return ref.type;
}

function usersetToFga(
  userset: any,
  metadata: any,
  relationName: string,
): string {
  if (!userset) return '';

  if (userset.this !== undefined) {
    const directTypes: any[] =
      metadata?.relations?.[relationName]?.directly_related_user_types ?? [];
    if (directTypes.length === 0) return '[user]';
    return `[${directTypes.map(typeRefToString).join(', ')}]`;
  }

  if (userset.union) {
    return (userset.union.child ?? [])
      .map((c: any) => usersetToFga(c, metadata, relationName))
      .join(' or ');
  }

  if (userset.intersection) {
    return (userset.intersection.child ?? [])
      .map((c: any) => usersetToFga(c, metadata, relationName))
      .join(' and ');
  }

  if (userset.difference) {
    const base = usersetToFga(userset.difference.base, metadata, relationName);
    const sub = usersetToFga(
      userset.difference.subtract,
      metadata,
      relationName,
    );
    return `${base} but not ${sub}`;
  }

  if (userset.computedUserset) {
    return userset.computedUserset.relation ?? '';
  }

  if (userset.tupleToUserset) {
    const computed = userset.tupleToUserset.computedUserset?.relation ?? '';
    const tupleset = userset.tupleToUserset.tupleset?.relation ?? '';
    return `${computed} from ${tupleset}`;
  }

  return JSON.stringify(userset);
}

function modelToFgaDsl(model: any): string {
  if (!model) return '';
  const lines: string[] = [];
  lines.push('model');
  lines.push(`  schema ${model.schema_version ?? '1.1'}`);
  lines.push('');

  for (const typeDef of model.type_definitions ?? []) {
    lines.push(`type ${typeDef.type}`);
    const relations = Object.entries(typeDef.relations ?? {});
    if (relations.length > 0) {
      lines.push('  relations');
      for (const [name, def] of relations) {
        const expr = usersetToFga(def, typeDef.metadata, name);
        lines.push(`    define ${name}: ${expr}`);
      }
    }
    lines.push('');
  }

  return lines.join('\n').trimEnd();
}

// ─── Cards view ───────────────────────────────────────────────────────────────

function renderRelationDefinition(def: any): string {
  if (!def) return '—';
  if (def.this !== undefined) return 'direct';
  if (def.union) {
    return `union(${(def.union.child ?? [])
      .map((c: any) => renderRelationDefinition(c))
      .join(', ')})`;
  }
  if (def.intersection) {
    return `intersection(${(def.intersection.child ?? [])
      .map((c: any) => renderRelationDefinition(c))
      .join(', ')})`;
  }
  if (def.difference) {
    return `difference(base=${renderRelationDefinition(
      def.difference.base,
    )}, subtract=${renderRelationDefinition(def.difference.subtract)})`;
  }
  if (def.computedUserset) return `computed(${def.computedUserset.relation})`;
  if (def.tupleToUserset) {
    return `ttu(${def.tupleToUserset.tupleset?.relation} → ${def.tupleToUserset.computedUserset?.relation})`;
  }
  return JSON.stringify(def);
}

function TypeCard({ typeDef }: { typeDef: FgaTypeDefinition }) {
  const [open, setOpen] = useState(false);
  const relations = Object.entries(typeDef.relations ?? {});

  return (
    <Paper
      elevation={0}
      style={{ marginBottom: 12, border: '1px solid #e0e0e0', borderRadius: 4 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: '12px 16px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography
            variant="subtitle1"
            style={{ fontFamily: 'monospace', fontWeight: 700 }}
          >
            {typeDef.type}
          </Typography>
          <Chip
            size="small"
            label={`${relations.length} relation${
              relations.length !== 1 ? 's' : ''
            }`}
          />
        </Box>
        <IconButton size="small">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Divider />
        <List dense style={{ paddingLeft: 24 }}>
          {relations.length === 0 && (
            <ListItem>
              <ListItemText secondary="No relations defined" />
            </ListItem>
          )}
          {relations.map(([name, def]) => (
            <ListItem key={name}>
              <ListItemText
                primary={
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {name}
                  </span>
                }
                secondary={
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                      color: '#666',
                    }}
                  >
                    {renderRelationDefinition(def)}
                  </span>
                }
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
}

// ─── Graph view ───────────────────────────────────────────────────────────────

interface GraphEdge {
  id: string;
  from: string;
  to: string;
  kind: 'direct' | 'ttu';
  labels: string[];
}

interface NodePos {
  x: number;
  y: number;
  w: number;
  h: number;
}

function extractEdges(types: FgaTypeDefinition[]): GraphEdge[] {
  const edgeMap = new Map<string, GraphEdge>();

  function addEdge(
    from: string,
    to: string,
    kind: 'direct' | 'ttu',
    label: string,
  ) {
    const key = `${from}->${to}:${kind}`;
    if (!edgeMap.has(key)) {
      edgeMap.set(key, { id: key, from, to, kind, labels: [] });
    }
    const e = edgeMap.get(key)!;
    if (!e.labels.includes(label)) e.labels.push(label);
  }

  function walk(
    userset: any,
    metadata: any,
    typeName: string,
    relationName: string,
  ) {
    if (!userset) return;

    if (userset.this !== undefined) {
      const directTypes: any[] =
        metadata?.relations?.[relationName]?.directly_related_user_types ?? [];
      for (const dt of directTypes) {
        if (!dt.wildcard) addEdge(dt.type, typeName, 'direct', relationName);
      }
      return;
    }

    if (userset.tupleToUserset) {
      const tupleset = userset.tupleToUserset.tupleset?.relation ?? '';
      const computed = userset.tupleToUserset.computedUserset?.relation ?? '';
      const typeDef = types.find(t => t.type === typeName);
      if (typeDef) {
        const meta = (typeDef as any).metadata;
        const pts: any[] =
          meta?.relations?.[tupleset]?.directly_related_user_types ?? [];
        for (const dt of pts) {
          if (!dt.wildcard && !dt.relation) {
            addEdge(dt.type, typeName, 'ttu', `${computed} from ${tupleset}`);
          }
        }
      }
      return;
    }

    const children: any[] = [
      ...(userset.union?.child ?? []),
      ...(userset.intersection?.child ?? []),
      ...(userset.difference
        ? [userset.difference.base, userset.difference.subtract]
        : []),
    ];
    for (const child of children) walk(child, metadata, typeName, relationName);
  }

  for (const typeDef of types) {
    for (const [relName, def] of Object.entries(typeDef.relations ?? {})) {
      walk(def, (typeDef as any).metadata, typeDef.type, relName);
    }
  }

  return Array.from(edgeMap.values());
}

function computeLayout(types: FgaTypeDefinition[]): Record<string, NodePos> {
  const W = 130;
  const H = 44;
  const positions: Record<string, NodePos> = {};

  const SUBJECT_TYPES = ['user', 'group'];
  const INFRA_TYPES = ['tenant'];

  const subjects = types.filter(t => SUBJECT_TYPES.includes(t.type));
  const infra = types.filter(t => INFRA_TYPES.includes(t.type));
  const resources = types.filter(
    t => !SUBJECT_TYPES.includes(t.type) && !INFRA_TYPES.includes(t.type),
  );

  // subjects: left column, top
  subjects.forEach((t, i) => {
    positions[t.type] = { x: 40, y: 50 + i * 90, w: W, h: H };
  });

  // tenant: below subjects, same left column
  infra.forEach((t, i) => {
    positions[t.type] = {
      x: 40 + i * 170,
      y: 50 + subjects.length * 90 + 40,
      w: W,
      h: H,
    };
  });

  // resources: 2-column grid on the right
  resources.forEach((t, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    positions[t.type] = { x: 320 + col * 180, y: 40 + row * 90, w: W, h: H };
  });

  return positions;
}

const NODE_PALETTE: Record<
  string,
  { fill: string; stroke: string; text: string }
> = {
  user: { fill: '#e8f5e9', stroke: '#43a047', text: '#1b5e20' },
  group: { fill: '#e3f2fd', stroke: '#1e88e5', text: '#0d47a1' },
  tenant: { fill: '#fff8e1', stroke: '#ffb300', text: '#e65100' },
  _default: { fill: '#f3e5f5', stroke: '#8e24aa', text: '#4a148c' },
};

function nodeColor(typeName: string) {
  return NODE_PALETTE[typeName] ?? NODE_PALETTE._default;
}

// Computes the point where the line from (ox,oy) to (tx,ty) exits/enters a rect.
function rectEdgePoint(
  ox: number,
  oy: number,
  tx: number,
  ty: number,
  p: NodePos,
): { x: number; y: number } {
  const cx = p.x + p.w / 2;
  const cy = p.y + p.h / 2;
  const hw = p.w / 2 + 4;
  const hh = p.h / 2 + 4;
  const dx = tx - ox;
  const dy = ty - oy;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (absDx === 0 && absDy === 0) return { x: cx, y: cy };
  const t = absDx * hh > absDy * hw ? hw / absDx : hh / absDy;
  return { x: cx + dx * t, y: cy + dy * t };
}

// drag state: null = no drag, canvas = panning, node name = dragging that node
type DragState =
  | null
  | { kind: 'canvas'; mx: number; my: number; px: number; py: number }
  | { kind: 'node'; name: string; ox: number; oy: number };

function GraphViewer({ types }: { types: FgaTypeDefinition[] }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [pan, setPan] = useState({ x: 20, y: 20 });
  const [scale, setScale] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const [positions, setPositions] = useState<Record<string, NodePos>>(() =>
    computeLayout(types),
  );
  const drag = useRef<DragState>(null);

  const edges = useMemo(() => extractEdges(types), [types]);

  // non-passive wheel for zoom
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setScale(s =>
        Math.min(3, Math.max(0.25, s * (e.deltaY > 0 ? 0.9 : 1.1))),
      );
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Convert client coords to SVG-canvas coords (undoes pan + scale)
  function toCanvas(clientX: number, clientY: number) {
    const svg = svgRef.current;
    if (!svg) return { cx: 0, cy: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      cx: (clientX - rect.left - pan.x) / scale,
      cy: (clientY - rect.top - pan.y) / scale,
    };
  }

  function onMouseDown(e: React.MouseEvent<SVGSVGElement>) {
    // Check if the pointer is over a node <g> by walking up from target
    let el: Element | null = e.target as Element;
    while (el && el !== svgRef.current) {
      if (el.getAttribute('data-node')) {
        const name = el.getAttribute('data-node')!;
        const pos = positions[name];
        if (!pos) break;
        const { cx, cy } = toCanvas(e.clientX, e.clientY);
        drag.current = { kind: 'node', name, ox: cx - pos.x, oy: cy - pos.y };
        e.stopPropagation();
        return;
      }
      el = el.parentElement;
    }
    // Canvas pan
    drag.current = {
      kind: 'canvas',
      mx: e.clientX,
      my: e.clientY,
      px: pan.x,
      py: pan.y,
    };
  }

  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d) return;

    if (d.kind === 'canvas') {
      setPan({ x: d.px + e.clientX - d.mx, y: d.py + e.clientY - d.my });
      return;
    }

    if (d.kind === 'node') {
      const { cx, cy } = toCanvas(e.clientX, e.clientY);
      setPositions(prev => {
        const old = prev[d.name];
        if (!old) return prev;
        return { ...prev, [d.name]: { ...old, x: cx - d.ox, y: cy - d.oy } };
      });
    }
  }

  function onMouseUp(e: React.MouseEvent<SVGSVGElement>) {
    // If drag distance was tiny → treat as click (toggle selection)
    const d = drag.current;
    if (d?.kind === 'node') {
      const pos = positions[d.name];
      if (pos) {
        const { cx, cy } = toCanvas(e.clientX, e.clientY);
        const dist = Math.hypot(cx - d.ox - pos.x, cy - d.oy - pos.y);
        if (dist < 4) setSelected(sel => (sel === d.name ? null : d.name));
      }
    }
    drag.current = null;
  }

  const isDragging = drag.current !== null;

  return (
    <Box style={{ width: '100%' }}>
      <Box
        style={{
          width: '100%',
          height: 530,
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          overflow: 'hidden',
          background: '#fafafa',
          cursor: isDragging ? 'grabbing' : 'grab',
          position: 'relative',
        }}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => {
            drag.current = null;
          }}
          style={{ display: 'block', userSelect: 'none' }}
        >
          <defs>
            <marker
              id="g-arr-def"
              markerWidth="7"
              markerHeight="7"
              refX="5"
              refY="3.5"
              orient="auto"
            >
              <path d="M0,0.5 L0,6.5 L7,3.5 z" fill="#bdbdbd" />
            </marker>
            <marker
              id="g-arr-sel"
              markerWidth="7"
              markerHeight="7"
              refX="5"
              refY="3.5"
              orient="auto"
            >
              <path d="M0,0.5 L0,6.5 L7,3.5 z" fill="#1976d2" />
            </marker>
            <marker
              id="g-arr-ttu"
              markerWidth="7"
              markerHeight="7"
              refX="5"
              refY="3.5"
              orient="auto"
            >
              <path d="M0,0.5 L0,6.5 L7,3.5 z" fill="#8e24aa" />
            </marker>
          </defs>

          <g transform={`translate(${pan.x},${pan.y}) scale(${scale})`}>
            {/* Edges — rendered below nodes */}
            {edges.map(edge => {
              const fp = positions[edge.from];
              const tp = positions[edge.to];
              if (!fp || !tp) return null;

              const fcx = fp.x + fp.w / 2;
              const fcy = fp.y + fp.h / 2;
              const tcx = tp.x + tp.w / 2;
              const tcy = tp.y + tp.h / 2;

              const start = rectEdgePoint(tcx, tcy, fcx, fcy, fp);
              const end = rectEdgePoint(fcx, fcy, tcx, tcy, tp);
              const midX = (start.x + end.x) / 2;
              const midY = (start.y + end.y) / 2;

              const isTtu = edge.kind === 'ttu';
              const active =
                !selected || edge.from === selected || edge.to === selected;
              const isSelected = active && !!selected;
              let edgeColor: string;
              let markerId: string;
              if (isTtu) {
                edgeColor = '#8e24aa';
                markerId = 'g-arr-ttu';
              } else if (isSelected) {
                edgeColor = '#1976d2';
                markerId = 'g-arr-sel';
              } else {
                edgeColor = '#bdbdbd';
                markerId = 'g-arr-def';
              }
              const label =
                edge.labels.slice(0, 2).join(', ') +
                (edge.labels.length > 2 ? '…' : '');

              return (
                <g
                  key={edge.id}
                  opacity={active ? 1 : 0.08}
                  style={{ pointerEvents: 'none' }}
                >
                  <line
                    x1={start.x}
                    y1={start.y}
                    x2={end.x}
                    y2={end.y}
                    stroke={edgeColor}
                    strokeWidth={1.5}
                    strokeDasharray={isTtu ? '5 3' : undefined}
                    markerEnd={`url(#${markerId})`}
                  />
                  <text
                    x={midX}
                    y={midY - 4}
                    fontSize={8}
                    fill={isTtu ? '#6a1b9a' : '#9e9e9e'}
                    textAnchor="middle"
                    style={{ fontFamily: 'monospace' }}
                  >
                    {label}
                  </text>
                </g>
              );
            })}

            {/* Nodes — rendered on top so they receive mouse events */}
            {Object.entries(positions).map(([typeName, pos]) => {
              const c = nodeColor(typeName);
              const isSel = selected === typeName;
              return (
                <g
                  key={typeName}
                  data-node={typeName}
                  style={{ cursor: 'grab' }}
                >
                  <rect
                    x={pos.x}
                    y={pos.y}
                    width={pos.w}
                    height={pos.h}
                    rx={6}
                    ry={6}
                    fill={isSel ? c.stroke : c.fill}
                    stroke={c.stroke}
                    strokeWidth={isSel ? 2.5 : 1.5}
                  />
                  <text
                    x={pos.x + pos.w / 2}
                    y={pos.y + pos.h / 2 + 5}
                    fontSize={12}
                    fontWeight="bold"
                    textAnchor="middle"
                    fill={isSel ? '#fff' : c.text}
                    style={{ fontFamily: 'monospace', pointerEvents: 'none' }}
                  >
                    {typeName}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {/* Legend */}
        <Box
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.93)',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            padding: '6px 12px',
            fontSize: 10,
            lineHeight: '20px',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 2,
            }}
          >
            <svg width="28" height="12" style={{ overflow: 'visible' }}>
              <line
                x1="2"
                y1="6"
                x2="22"
                y2="6"
                stroke="#bdbdbd"
                strokeWidth="1.5"
              />
              <polygon points="21,3 28,6 21,9" fill="#bdbdbd" />
            </svg>
            <span style={{ color: '#555' }}>direct assignment</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 4,
            }}
          >
            <svg width="28" height="12" style={{ overflow: 'visible' }}>
              <line
                x1="2"
                y1="6"
                x2="22"
                y2="6"
                stroke="#8e24aa"
                strokeWidth="1.5"
                strokeDasharray="4 2"
              />
              <polygon points="21,3 28,6 21,9" fill="#8e24aa" />
            </svg>
            <span style={{ color: '#555' }}>tuple-to-userset (TTU)</span>
          </div>
          <div style={{ color: '#aaa', fontStyle: 'italic' }}>
            drag node · click node · scroll zoom · drag canvas
          </div>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type ViewMode = 'cards' | 'fga' | 'graph';

export function ModelViewer({ client }: { client: RebacExplorerClient }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('fga');

  useEffect(() => {
    client
      .getModel()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [client]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography color="error" style={{ padding: 16 }}>
        Error: {error}
      </Typography>
    );
  if (!data?.model)
    return <Typography style={{ padding: 16 }}>No model loaded.</Typography>;

  const { storeId, modelId, model } = data;
  const types: FgaTypeDefinition[] = model.type_definitions ?? [];

  return (
    <Box style={{ padding: 24, maxWidth: 960 }}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ marginBottom: 16 }}
      >
        <Typography variant="h6">Authorization Model</Typography>
        <ButtonGroup size="small" variant="outlined">
          <Button
            color={viewMode === 'fga' ? 'primary' : 'default'}
            variant={viewMode === 'fga' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('fga')}
          >
            .fga
          </Button>
          <Button
            color={viewMode === 'cards' ? 'primary' : 'default'}
            variant={viewMode === 'cards' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('cards')}
          >
            Cards
          </Button>
          <Button
            color={viewMode === 'graph' ? 'primary' : 'default'}
            variant={viewMode === 'graph' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('graph')}
          >
            Graph
          </Button>
        </ButtonGroup>
      </Box>

      {/* Chips */}
      <Box
        display="flex"
        style={{ gap: 8, flexWrap: 'wrap', marginBottom: 24 }}
      >
        <Chip size="small" label={`Store: ${storeId}`} variant="outlined" />
        {modelId && (
          <Chip
            size="small"
            label={`Model: ${modelId}`}
            variant="outlined"
            color="primary"
          />
        )}
        <Chip
          size="small"
          label={`Schema: ${model.schema_version}`}
          variant="outlined"
        />
        <Chip size="small" label={`${types.length} types`} />
      </Box>

      {/* Cards view */}
      {viewMode === 'cards' &&
        types.map(t => <TypeCard key={t.type} typeDef={t} />)}

      {/* FGA DSL view */}
      {viewMode === 'fga' && (
        <Paper
          elevation={0}
          style={{
            padding: 20,
            backgroundColor: '#1e1e1e',
            borderRadius: 4,
            overflowX: 'auto',
          }}
        >
          <pre
            style={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              color: '#d4d4d4',
              whiteSpace: 'pre',
            }}
          >
            {modelToFgaDsl(model)}
          </pre>
        </Paper>
      )}

      {/* Graph view */}
      {viewMode === 'graph' && <GraphViewer types={types} />}
    </Box>
  );
}
